import {useCallback, useEffect, useRef} from 'react';
import {useEvent} from './use-event';

type UseLoadMoreParams = {
  hasNextPage: boolean;
  loading: boolean;
  disabled?: boolean;
  onLoadMore: () => void;
  threshold?: number;
  rootMargin?: string;
};

export function useLoadMore<T extends HTMLElement = HTMLDivElement>({
  hasNextPage,
  loading,
  disabled,
  onLoadMore,
  threshold = 0.1,
  rootMargin,
}: UseLoadMoreParams) {
  const elementRef = useRef<T | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const handleLoadMore = useEvent(onLoadMore);

  const cleanupObserver = useCallback(() => {
    observerRef.current?.disconnect();
    observerRef.current = null;
  }, []);

  const observeElement = useCallback(
    (element: T | null) => {
      cleanupObserver();

      if (!element || typeof IntersectionObserver === 'undefined') return;

      const observer = new IntersectionObserver(
        entries => {
          const entry = entries[0];
          if (!entry?.isIntersecting || !hasNextPage || loading || disabled) return;
          handleLoadMore();
        },
        {threshold, rootMargin},
      );

      observer.observe(element);
      observerRef.current = observer;
    },
    [
      cleanupObserver,
      disabled,
      handleLoadMore,
      hasNextPage,
      loading,
      rootMargin,
      threshold,
    ],
  );

  const loadMoreRef = useCallback(
    (element: T | null) => {
      elementRef.current = element;
      observeElement(element);
    },
    [observeElement],
  );

  useEffect(() => {
    return cleanupObserver;
  }, [cleanupObserver]);

  return [loadMoreRef] as const;
}
